import {Injectable} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {
  getAmexAccountType,
  getAmexF2FDirectSENumber,
  getAmexSENumber,
  getGatewayName,
  LocationApiKey,
  LocationApplePayPaymentProcessingCertificate,
  LocationConfig,
  LocationRoute
} from "../models/locationconfig";
import {Gateway} from "../models/gateway";
import {RemoteTerminalStatus, Status} from "../models/status";
import {UserService} from "./user.service";
import {User} from "../models/user";
import {v4 as uuidv4} from 'uuid';
import {AmexAccountType} from "../models/amex-account";
import {getTargetSchemes, KnownRoutes} from "../models/known-routes";

export const PASSWORD_PLACEHOLDER = '************';

export const DEFAULT_LOCATION_CONFIG: LocationConfig = {
  merchant: {
    id: '',
    status: Status.Active
  },
  location: {
    id: '',
    status: Status.Active,
    name: '',
    description: '',
    partnerName: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    postcode: '',
    countryCode: 'GB',
    emailAddress: '',
    phoneNumber: '',
    marketId: '',
    URL: '',

    isMerchantInitiatedTransactionsEnabled: false,
    isMotoEnabled: false,
    isMotoAllowedWithoutCVV: false,
    isApplePayEnabled: false,
    isGooglePayEnabled: false,
    isAlipayEnabled: false,

    gatewayUsername: '',
    encryptedGatewayPassword: '',
    merchantCategoryCode: '',
    domainNames: [],

    routes: [
      {
        scheme: 'visa',
        enabled: true,
        merchantId: '',
        gatewayName: 'Mars'
      }
    ],

    apiKeys: [],
    applePayPaymentProcessingCertificates: [],
    applePayCertificateSigningRequests: [],

    createdBy: '',
    createdDateTime: '',
    lastModifiedBy: '',
    lastModifiedDateTime: ''
  },

  remoteTerminal: undefined
};

@Injectable({ providedIn: 'root' })
export class LocationMapperService {
  user: User;
  applePayPPC: LocationApplePayPaymentProcessingCertificate[];
  originalStruct: string;

  constructor(private fb: FormBuilder,
              private userService: UserService,
  ) {
    this.userService.getUserRole().then((user) => {
      this.user = user;
    });
  }

  createFormModel(data?: LocationConfig): FormGroup {
    const locationConfig = data || DEFAULT_LOCATION_CONFIG;
    const gatewayName = getGatewayName(locationConfig.location) || Gateway.Mars;

    // Store the original for later comparison when saving
    this.originalStruct = JSON.stringify(locationConfig);

    let amexType = getAmexAccountType(locationConfig.location);
    let amexEcomSE = getAmexSENumber(locationConfig.location);
    let amexF2FDirectSE = getAmexF2FDirectSENumber(locationConfig.location);

    const domainNames = this.fb.control(locationConfig.location.domainNames || []);

    const apiKeysArray = this.fb.array(
      (locationConfig.location.apiKeys || []).map(key => this.createApiKeyGroup(key, false))
    );

    this.applePayPPC = locationConfig.location.applePayPaymentProcessingCertificates || [];

    // Show placeholder if an encrypted password exists, empty string otherwise
    const passwordDisplayValue = locationConfig.location.encryptedGatewayPassword
      ? PASSWORD_PLACEHOLDER
      : '';

    return this.fb.group({
      id: [locationConfig.location.id, Validators.required],
      customerId: [locationConfig.merchant.id],

      // Always validated
      status: [locationConfig.location.status || Status.Active, Validators.required],
      name: [locationConfig.location.name, Validators.required],
      gatewayName: [gatewayName, Validators.required],
      emailAddress: [locationConfig.location.emailAddress, [Validators.email]],
      postcode: [locationConfig.location.postcode, Validators.required],
      countryCode: [locationConfig.location.countryCode, Validators.required],
      partnerName: [locationConfig.location.partnerName, Validators.required],

      // Validated conditionally later
      gatewayUsername: [locationConfig.location.gatewayUsername],
      editableGatewayPassword: [passwordDisplayValue],
      encryptedGatewayPassword: [locationConfig.location.encryptedGatewayPassword],
      url: [locationConfig.location.URL],
      isApplePayEnabled: [locationConfig.location.isApplePayEnabled],
      isGooglePayEnabled: [locationConfig.location.isGooglePayEnabled],
      isAlipayEnabled: [locationConfig.location.isAlipayEnabled],
      amexAccountType: [amexType],
      amexEcomSENumber: [amexEcomSE],
      amexF2FSENumber: [amexF2FDirectSE],

      // Generally editable
      remoteTerminalId: [locationConfig.remoteTerminal?.status == RemoteTerminalStatus.Active ? locationConfig.location.remoteTerminalId : ''],
      description: [locationConfig.location.description],
      isMerchantInitiatedTransactionsEnabled: [locationConfig.location.isMerchantInitiatedTransactionsEnabled],
      isMotoEnabled: [locationConfig.location.isMotoEnabled],
      isMotoAllowedWithoutCVV: [locationConfig.location.isMotoAllowedWithoutCVV],
      merchantCategoryCode: [locationConfig.location.merchantCategoryCode],
      address1: [locationConfig.location.address1],
      address2: [locationConfig.location.address2],
      city: [locationConfig.location.city],
      state: [locationConfig.location.state],
      marketId: [locationConfig.location.marketId],
      phoneNumber: [locationConfig.location.phoneNumber],
      createdBy: [locationConfig.location.createdBy],
      createdDateTime: [locationConfig.location.createdDateTime],
      lastModifiedBy: [locationConfig.location.lastModifiedBy],
      lastModifiedDateTime: [locationConfig.location.lastModifiedDateTime],

      domainNames: domainNames,
      apiKeys: apiKeysArray,
    });
  }

  createApiKeyGroup(key?: LocationApiKey, isNew = true): FormGroup {
    return this.fb.group({
      key: [{ value: key?.id || uuidv4(), disabled: !isNew}, Validators.required],
      enabled: [key?.enabled ?? true],
      isNew: [isNew], // Internal UI flag to track "unsaved" status
      createdBy: [key?.createdBy || this.user?.Email || ''],
      createdDateUtc: [key?.createdDateTime || new Date().toISOString()],
      lastModifiedBy: [key?.lastModifiedBy || this.user?.Email || ''],
      lastModifiedDateUtc: [key?.lastModifiedDateTime || new Date().toISOString()],
    });
  }

  toBackendModel(formValue: any): LocationConfig {
    // Initialise the base structure using original metadata to preserve read-only fields
    const originalStruct: LocationConfig = JSON.parse(this.originalStruct);
    const backendModel: LocationConfig = {
      ...originalStruct,
      location: {
        ...originalStruct.location,
        id: formValue.id,
        status: formValue.status,
        name: formValue.name,
        emailAddress: formValue.emailAddress,
        postcode: formValue.postcode,
        countryCode: formValue.countryCode,
        partnerName: formValue.partnerName,
        remoteTerminalId: formValue.remoteTerminalId,
        description: formValue.description,
        merchantCategoryCode: formValue.merchantCategoryCode,
        address1: formValue.address1,
        address2: formValue.address2,
        city: formValue.city,
        state: formValue.state,
        marketId: formValue.marketId,
        phoneNumber: formValue.phoneNumber,
        domainNames: formValue.domainNames,
        gatewayUsername: formValue.gatewayUsername,
        encryptedGatewayPassword: this.resolveGatewayPassword(formValue, originalStruct),
        URL: formValue.url,
        isApplePayEnabled: formValue.isApplePayEnabled,
        isGooglePayEnabled: formValue.isGooglePayEnabled,
        isAlipayEnabled: formValue.isAlipayEnabled,
        isMerchantInitiatedTransactionsEnabled: formValue.isMerchantInitiatedTransactionsEnabled,
        isMotoEnabled: formValue.isMotoEnabled,
        isMotoAllowedWithoutCVV: formValue.isMotoAllowedWithoutCVV,
        createdBy: originalStruct.location.createdBy || this.user?.Email || 'admin_tool_system',
        createdDateTime: originalStruct.location.createdDateTime || new Date().toISOString(),
        lastModifiedBy: originalStruct.location.lastModifiedBy || this.user?.Email || 'admin_tool_system',
        lastModifiedDateTime: originalStruct.location.lastModifiedDateTime || new Date().toISOString(),
      },
      remoteTerminal: {
        ...originalStruct.remoteTerminal,
        id: formValue.remoteTerminalId.length > 0 ? formValue.remoteTerminalId : originalStruct.remoteTerminal?.id || `rag-${formValue.id}`,
        status: formValue.remoteTerminalId.length > 0 ? RemoteTerminalStatus.Active : RemoteTerminalStatus.Inactive,
        type: 'remote',
      },
    };

    // Paymentsense locations do not have a customer ID, but to satisfy the backend model we set it to a prefixed value.
    // Only set this if the existing record does not already have a CustomerId.
    if (formValue.gatewayName == Gateway.PayVector || formValue.gatewayName == Gateway.Cybersource) {
      if (!originalStruct.merchant.id && formValue.id && formValue.id.trim().length > 0) {
        backendModel.merchant.id = `rag-${formValue.id}`;
      }
    }

    // Recompose API Keys Array
    backendModel.location.apiKeys = (formValue.apiKeys || []).map((key: any) => ({
      id: key.key,
      enabled: key.enabled,
      createdBy: key.createdBy,
      createdDateTime: key.createdDateUtc,
      lastModifiedBy: key.lastModifiedBy,
      lastModifiedDateTime: key.lastModifiedDateUtc
    }));

    // Recompose Location Routes with Gateway and Amex Logic
    const existingRoutes = originalStruct.location.routes || [];
    const targetSchemes = getTargetSchemes(existingRoutes, formValue.amexAccountType !== AmexAccountType.None);
    if (formValue.amexAccountType === AmexAccountType.Direct) {
      targetSchemes.push(KnownRoutes.AmexF2FDirect);
    }
    const amexScheme = targetSchemes.find(scheme => scheme === KnownRoutes.Amex || scheme === KnownRoutes.AmexEcom);
    const finalRoutes: LocationRoute[] = [];

    targetSchemes.forEach(scheme => {
      let route = existingRoutes.find(r => r.scheme === scheme);

      // The Merchant ID is the parent Location ID by default, but needs to be non-digits like "_ecomm" and leading zeros removed
      const routeMerchantID = formValue.id.replace(/[^0-9]/g, '').replace(/^0+/,'');

      if (!route) {
        route = {
          scheme: scheme,
          enabled: true,
          merchantId: routeMerchantID,
          gatewayName: formValue.gatewayName,
          // Not RemoteAG fields, but can't be null
          processorInstance: "",
          pinned: false
        };
      } else {
        route = {
          ...route,
          gatewayName: scheme === KnownRoutes.AmexF2FDirect ? route.gatewayName : formValue.gatewayName,
          enabled: true,
          // Preserve existing Amex Merchant ID if applicable
          merchantId: scheme == amexScheme || scheme === KnownRoutes.AmexF2FDirect ? route.merchantId : routeMerchantID,
        };
      }

      // Apply specific Amex Routing Rules
      if (scheme == amexScheme || scheme === KnownRoutes.AmexF2FDirect) {
        if (formValue.amexAccountType === AmexAccountType.Direct) {
          route.enabled = true;
          route.merchantId = scheme == amexScheme ? formValue.amexEcomSENumber : formValue.amexF2FSENumber;
        } else if (formValue.amexAccountType === AmexAccountType.PayFac) {
          route.enabled = true;
          route.merchantId = routeMerchantID;
        } else {
          route.enabled = false;
        }
      } else {
        // Standard schemes always use parent Location ID
        route.merchantId = routeMerchantID;
      }

      finalRoutes.push(route);
    });

    // Add routes that are not Remote AG routes
    existingRoutes.forEach(route => {
      if (targetSchemes.findIndex((r) => route.scheme as KnownRoutes === r) < 0) {
        finalRoutes.push(route);
      }
    });

    backendModel.location.routes = finalRoutes;
    return backendModel;
  }

  private resolveGatewayPassword(formValue: any, original: LocationConfig): string {
    const editableValue = formValue.editableGatewayPassword;

    if (!editableValue || editableValue === '') {
      // User cleared the field — submit blank
      return '';
    }
    if (editableValue === PASSWORD_PLACEHOLDER) {
      // Unchanged — submit existing encrypted password
      return original.location.encryptedGatewayPassword || '';
    }
    // New plaintext value — submit as-is for the backend to encrypt
    return editableValue;
  }
}
